import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";
import { Alert, Button, Card, CardHeader, Field, Input, Mono, PageHeader } from "../components/ui";

interface EnrollResult {
  secret: string;
  otpauthUri: string;
  recoveryCodes: string[];
}

type Phase =
  | { step: "idle" }
  | { step: "password" }
  | { step: "setup"; otpauthUri: string; recoveryCodes: string[]; secret: string }
  | { step: "success" }
  | { step: "disable" };

const STEPS = [
  { label: "Password", description: "Confirm your identity" },
  { label: "Scan QR", description: "Add to authenticator app" },
  { label: "Verify", description: "Confirm setup" },
] as const;

export function AccountSettingsPage() {
  const { user, reloadSession } = useAuth();
  const [phase, setPhase] = useState<Phase>({ step: "idle" });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [codesSaved, setCodesSaved] = useState(false);

  const totpEnabled = user?.totpEnabled ?? false;

  // Render the QR once the enrollment returns the otpauth URI.
  useEffect(() => {
    let cancelled = false;
    if (phase.step !== "setup") return;
    setQrDataUrl(null);
    void QRCode.toDataURL(phase.otpauthUri, { width: 220, margin: 1 }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  async function beginEnrollment(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api<EnrollResult>("/v1/auth/2fa/enroll", {
        method: "POST",
        body: { password },
      });
      setPhase({ 
        step: "setup", 
        otpauthUri: result.otpauthUri, 
        recoveryCodes: result.recoveryCodes,
        secret: result.secret 
      });
      setCodesSaved(false);
      setCopied(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start enrollment.");
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmEnrollment(event: FormEvent) {
    event.preventDefault();
    if (phase.step !== "setup") return;
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/2fa/confirm", { method: "POST", body: { code } });
      setPhase({ step: "success" });
      setPassword("");
      setCode("");
      setTimeout(async () => {
        await reloadSession();
        setPhase({ step: "idle" });
      }, 2000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That code did not match.");
    } finally {
      setSubmitting(false);
    }
  }

  async function disable(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/v1/auth/2fa/disable", { method: "POST", body: { password, code } });
      setPhase({ step: "idle" });
      setPassword("");
      setCode("");
      await reloadSession();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not disable two-factor authentication.");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCodes() {
    if (phase.step !== "setup") return;
    await navigator.clipboard.writeText(phase.recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadCodes() {
    if (phase.step !== "setup") return;
    const content = `Maxlayer Recovery Codes\n${"=".repeat(50)}\n\n${phase.recoveryCodes.join("\n")}\n\n${"=".repeat(50)}\nSave these codes in a safe place. Each code can only be used once.\nIf you lose access to your authenticator app, you can use these codes to sign in.\n\nGenerated: ${new Date().toISOString()}\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `maxlayer-recovery-codes-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const currentStep = phase.step === "password" ? 0 : phase.step === "setup" ? 1 : -1;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Account security"
        description="Required before using the operator console."
      />

      <Card>
        <CardHeader
          title="Two-factor authentication"
          description={
            totpEnabled
              ? "On — every sign-in asks for a code from your authenticator app."
              : "Off — a password alone is enough to sign in."
          }
        />

        <div className="p-5">
          {/* Step indicator */}
          {currentStep >= 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between">
                {STEPS.map((step, i) => (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                          i < currentStep
                            ? "bg-accent text-white"
                            : i === currentStep
                            ? "bg-accent text-white"
                            : "bg-line text-fg-subtle"
                        }`}
                      >
                        {i < currentStep ? "✓" : i + 1}
                      </div>
                      <div className="mt-2 text-center">
                        <p className="text-xs font-medium text-fg">{step.label}</p>
                        <p className="text-xs text-fg-subtle">{step.description}</p>
                      </div>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`mx-2 h-0.5 w-12 transition-colors ${
                          i < currentStep ? "bg-accent" : "bg-line"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Idle state */}
          {phase.step === "idle" && (
            <div className="space-y-4">
              {totpEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 rounded-lg bg-mint/10 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mint/20">
                      <svg className="h-5 w-5 text-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-fg">Two-factor authentication is enabled</p>
                      <p className="text-sm text-fg-subtle">Your account is protected with an authenticator app.</p>
                    </div>
                  </div>
                  <Button variant="secondary" onClick={() => setPhase({ step: "disable" })}>
                    Disable two-factor authentication
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg bg-accent/5 p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                      <svg className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-fg">Protect your account</p>
                      <p className="text-sm text-fg-subtle">
                        Require a code from your authenticator app when you sign in.
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => setPhase({ step: "password" })}>
                    Enable two-factor authentication
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Password phase */}
          {phase.step === "password" && (
            <form onSubmit={beginEnrollment} className="space-y-4">
              {error && <Alert>{error}</Alert>}
              <div className="space-y-2">
                <p className="text-sm font-medium text-fg">Confirm your password</p>
                <p className="text-sm text-fg-subtle">
                  Confirm your password to turn this on. New recovery codes will replace any old ones.
                </p>
              </div>
              <Field label="Password" htmlFor="enroll-password">
                <Input
                  id="enroll-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <div className="flex gap-3">
                <Button type="submit" loading={submitting}>
                  Continue
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPhase({ step: "idle" });
                    setPassword("");
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Setup phase */}
          {phase.step === "setup" && (
            <div className="space-y-6">
              {error && <Alert>{error}</Alert>}
              
              {/* QR Code section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-fg">Scan QR code</p>
                  <p className="text-sm text-fg-subtle">
                    Scan this with your authenticator app, or enter the key by hand.
                  </p>
                </div>
                <div className="flex flex-col items-center gap-4 rounded-lg bg-canvas p-6">
                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt="QR code to scan with your authenticator app" className="size-56" />
                  ) : (
                    <div className="size-56 animate-pulse rounded-lg bg-line" />
                  )}
                  <div className="w-full space-y-2">
                    <p className="text-xs font-medium text-fg-subtle">Can't scan? Enter this key manually:</p>
                    <div className="flex items-center gap-2 rounded bg-surface p-3">
                      <Mono className="flex-1 break-all text-xs">{phase.secret}</Mono>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void navigator.clipboard.writeText(phase.secret);
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recovery codes section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-fg">Save recovery codes</p>
                  <p className="text-sm text-fg-subtle">
                    Use these to sign in if you lose your authenticator app. Each one works once.
                  </p>
                </div>
                <div className="rounded-lg border border-warn/30 bg-warn/5 p-4">
                  <div className="mb-3 flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-warn" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-fg">
                      <span className="font-medium">Important:</span> Save these codes in a safe place. They won't be shown again.
                    </p>
                  </div>
                  {/* One column until `sm`. A recovery code is
                      `xxxx-xxxx-xxxx-xxxx` — 19 monospace characters, about
                      176px at this size — and half of a 375px screen minus the
                      two nested paddings is roughly 150. Two columns clipped the
                      last block of every code on a phone, which is the one
                      screen where these are unrecoverable if you copy them down
                      wrong. */}
                  <div className="grid grid-cols-1 gap-2 rounded bg-canvas p-4 sm:grid-cols-2">
                    {phase.recoveryCodes.map((recovery) => (
                      <code key={recovery} className="rounded bg-surface px-2 py-1.5 text-center font-mono text-sm tabular-nums">
                        {recovery}
                      </code>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => void copyCodes()}>
                      {copied ? "Copied!" : "Copy all"}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={downloadCodes}>
                      Download
                    </Button>
                  </div>
                </div>
                
                {/* Confirmation checkbox */}
                <label className="flex items-start gap-3 rounded-lg bg-surface p-4">
                  <input
                    type="checkbox"
                    checked={codesSaved}
                    onChange={(e) => setCodesSaved(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                  />
                  <span className="text-sm text-fg">
                    I've saved these recovery codes in a safe place
                  </span>
                </label>
              </div>

              {/* Verification form */}
              <form onSubmit={confirmEnrollment} className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-fg">Verify setup</p>
                  <p className="text-sm text-fg-subtle">
                    Enter the 6-digit code from your authenticator app to confirm setup.
                  </p>
                </div>
                <Field label="Authentication code" htmlFor="confirm-code">
                  <Input
                    id="confirm-code"
                    inputMode="numeric"
                    placeholder="000000"
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    className="text-center text-lg tracking-widest"
                  />
                </Field>
                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    loading={submitting}
                    disabled={!codesSaved}
                  >
                    Turn on two-factor authentication
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setPhase({ step: "idle" });
                      setPassword("");
                      setCode("");
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Success state */}
          {phase.step === "success" && (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-mint/20">
                <svg className="h-8 w-8 text-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="space-y-2">
                <p className="text-lg font-medium text-fg">Two-factor authentication enabled</p>
                <p className="text-sm text-fg-subtle">
                  You'll need your authenticator app or a recovery code to sign in.
                </p>
              </div>
            </div>
          )}

          {/* Disable phase */}
          {phase.step === "disable" && (
            <form onSubmit={disable} className="space-y-4">
              {error && <Alert>{error}</Alert>}
              <div className="rounded-lg bg-danger/10 p-4">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="space-y-1">
                    <p className="font-medium text-danger">Disable two-factor authentication?</p>
                    <p className="text-sm text-fg-subtle">
                      Your password alone will then be enough to sign in.
                    </p>
                  </div>
                </div>
              </div>
              <Field label="Password" htmlFor="disable-password">
                <Input
                  id="disable-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <Field label="Authentication code or recovery code" htmlFor="disable-code">
                <Input
                  id="disable-code"
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="text-center text-lg tracking-widest"
                />
              </Field>
              <div className="flex gap-3">
                <Button type="submit" variant="danger" loading={submitting}>
                  Disable
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPhase({ step: "idle" });
                    setPassword("");
                    setCode("");
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
